import { fieldStyle, labelStyle, workflowStyle, buttonStyle } from './changeOrderStyles';

export default function ChangeOrderRecordWorkflows({
  item,
  jobId,
  customer,
  address,
  customerEmail,
  busyId,
  sendOpen,
  sendConfirm,
  setSendConfirm,
  onSend,
  onCloseSend,
  openPreview,
  voidOpen,
  voidReason,
  setVoidReason,
  voidConfirmed,
  setVoidConfirmed,
  onVoid,
  onCloseVoid,
  signatureOpen,
  signName,
  setSignName,
  signInitials,
  setSignInitials,
  signConsent,
  setSignConsent,
  onCountersign,
  onCloseSignature,
}) {
  return (
    <>
      {sendOpen && (
        <div style={workflowStyle}>
          <strong>Confirm before sending</strong>
          <p style={{ fontSize: 12, color: '#596a81', lineHeight: 1.5, margin: '6px 0 10px' }}>
            The source PDF is the document being sent. Verify the job, complete attachment set, and
            recipient. The recipient is fixed to this job’s current customer email.
          </p>
          <div style={{ display: 'grid', gap: 6, fontSize: 13, marginBottom: 10 }}>
            <div>
              <b>Job:</b> {jobId} · {customer} · {address}
            </div>
            <div>
              <b>Change order:</b> {item.title} · {item.change_number} · {item.page_count || '—'}{' '}
              pages
            </div>
            {item.imported_contract && (
              <div
                style={{
                  padding: '8px 10px',
                  borderLeft: '3px solid #1b6b56',
                  borderRadius: 5,
                  background: '#edf7f2',
                  color: '#235b49',
                }}
              >
                <b>Linked existing signed contract:</b> {item.imported_contract.contract_number} ·{' '}
                {item.imported_contract.title}
                {item.imported_contract.signed_on ? ` · Signed ${item.imported_contract.signed_on}` : ''}
              </div>
            )}
            <div>
              <b>Recipient (read-only):</b> {customerEmail || 'No customer email on this job'}
            </div>
          </div>
          <button type="button" onClick={() => openPreview(item)} style={buttonStyle('quiet')}>
            Open authenticated source PDF
          </button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              ['job', 'I confirm this is the correct customer job.'],
              ['document', 'I reviewed the entire PDF and all attachments.'],
              [
                'recipient',
                `I confirm the recipient is ${customerEmail || 'the current customer email on this job'}.`,
              ],
            ].map(([key, text]) => (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  marginTop: 9,
                  fontSize: 12,
                  width: '100%',
                }}
              >
                <input
                  type="checkbox"
                  checked={sendConfirm[key]}
                  onChange={(event) =>
                    setSendConfirm((value) => ({ ...value, [key]: event.target.checked }))
                  }
                />
                {text}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              onClick={() => onSend(item)}
              disabled={
                busyId === item.id || !customerEmail || !Object.values(sendConfirm).every(Boolean)
              }
              style={buttonStyle('primary')}
            >
              {busyId === item.id ? 'Sending…' : 'Send to customer'}
            </button>
            <button type="button" onClick={onCloseSend} style={buttonStyle('quiet')}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {voidOpen && (
        <div style={workflowStyle}>
          <strong>Void this record</strong>
          <p style={{ fontSize: 12, color: '#596a81' }}>
            Voiding is permanent; the record is retained and cannot be deleted. Completed change
            orders cannot be voided.
          </p>
          <textarea
            value={voidReason}
            onChange={(event) => setVoidReason(event.target.value)}
            placeholder="Reason for voiding"
            rows={3}
            style={fieldStyle}
          />
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              fontSize: 12,
              marginTop: 9,
            }}
          >
            <input
              type="checkbox"
              checked={voidConfirmed}
              onChange={(event) => setVoidConfirmed(event.target.checked)}
            />
            I understand this change order will be marked void and retained in the record.
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => onVoid(item)}
              disabled={busyId === item.id || !voidReason.trim() || !voidConfirmed}
              style={buttonStyle('danger')}
            >
              Confirm void
            </button>
            <button
              type="button"
              onClick={() => {
                setVoidReason('');
                setVoidConfirmed(false);
                onCloseVoid();
              }}
              style={buttonStyle('quiet')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {signatureOpen && (
        <div style={workflowStyle}>
          <strong>Contractor countersignature</strong>
          <p style={{ fontSize: 12, color: '#596a81', lineHeight: 1.5 }}>
            This electronic signature applies to the entire PDF, including all attachments. The
            signed PDF includes an electronic-signature record; original pages are retained.
          </p>
          <label style={labelStyle}>
            Full legal name
            <input
              maxLength={100}
              value={signName}
              onChange={(event) => setSignName(event.target.value)}
              autoComplete="name"
              style={fieldStyle}
            />
          </label>
          {!!item.initial_pages?.length && (
            <label style={{ ...labelStyle, marginTop: 10 }}>
              Initials for pages {item.initial_pages.join(', ')}
              <input
                value={signInitials}
                onChange={(event) => setSignInitials(event.target.value)}
                maxLength={8}
                style={fieldStyle}
              />
            </label>
          )}
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              fontSize: 12,
              marginTop: 10,
            }}
          >
            <input
              type="checkbox"
              checked={signConsent}
              onChange={(event) => setSignConsent(event.target.checked)}
            />
            I consent to electronically sign the entire document and its attachments.
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              onClick={() => onCountersign(item)}
              disabled={
                busyId === item.id ||
                !signName.trim() ||
                !signConsent ||
                (!!item.initial_pages?.length && !signInitials.trim())
              }
              style={buttonStyle('primary')}
            >
              {busyId === item.id ? 'Signing…' : 'Apply countersignature'}
            </button>
            <button type="button" onClick={onCloseSignature} style={buttonStyle('quiet')}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
